import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, FormControl, InputAdornment, Input, Button } from '@material-ui/core';
import classNames from 'classnames';

import { CSVUpload, Loaders } from 'components';
import { useNotify } from 'hooks';
import {
  S3ObjectInput,
  UserSegmentFragment,
  useUpdateUserSegmentMutation,
  useGetUserSegmentOthersPresignedUrlQuery,
} from 'api';
import { makeStyles } from '@material-ui/core/styles';

interface IProps {
  segment: UserSegmentFragment;
  onSave: () => void;
}

const useStyles = makeStyles(theme => ({
  inputForm: {
    marginTop: 0,
    marginBottom: theme.spacing(2),
  },
  inputAdornment: {
    paddingRight: theme.spacing(1),
  },
  errorState: {
    border: `2px solid ${theme.palette.error.main}`,
  },
}));

const OtherPeopleEdit: FC<IProps> = ({ segment, onSave }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const notify = useNotify();

  const [requestCSV, setRequestCSV] = useState(false);
  const [isError, setIsError] = useState(false);

  const { mutate: updateUserSegment, isLoading: isMutateLoading } = useUpdateUserSegmentMutation({
    onError: handleUserSegmentError,
    onSuccess: handleUploadCompleted,
  });

  const getUserSegmentOthersOptions = useGetUserSegmentOthersPresignedUrlQuery(
    { id: segment.id },
    {
      enabled: requestCSV,
      onError: notify.queryError,
      onSuccess: () => notify.info({ i18n: { i18nKey: 'common:requestSuccess' } }),
      onSettled: () => setRequestCSV(false),
    }
  );

  function handleUserSegmentError() {
    setIsError(true);
    notify.mutationError();
  }

  function handleUploadCompleted() {
    notify.info({
      i18n: { i18nKey: 'common:uploadSuccess' },
    });
    onSave();
  }

  function handleRequestCSV() {
    setRequestCSV(true);
  }

  function handleAdd(uploadFile: S3ObjectInput) {
    const { id, segment_status, title } = segment;

    updateUserSegment({
      data: {
        id,
        segment_status,
        title,
        others_csv: uploadFile,
      },
    });
  }

  function renderAdornment() {
    const { isLoading, data } = getUserSegmentOthersOptions;
    const presignedObject = data?.getUserSegmentOthersPresignedUrl;

    if (isLoading) {
      return <Loaders.Standard display="inline" />;
    }

    if (presignedObject) {
      return (
        <Button href={presignedObject.url} color="primary" variant="contained" size="small">
          {t('admin:userSegmentCSVDownload')}
        </Button>
      );
    }

    return (
      <Button onClick={handleRequestCSV} disabled={isLoading} size="small">
        {t('admin:userSegmentCSVRequest')}
      </Button>
    );
  }

  function renderCSVUpload() {
    if (isMutateLoading) {
      return <Loaders.Standard display="inline" height={38} />;
    }

    return <CSVUpload onS3Object={uploadFile => handleAdd(uploadFile)} />;
  }

  return (
    <Grid container>
      <Grid item xs={3}>
        <Grid container alignItems="center">
          <Grid item>
            <Typography variant="subtitle2">{t('admin:userSegmentOtherPeople')}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth margin="normal" className={classes.inputForm}>
          <Input
            disabled
            disableUnderline
            color="primary"
            value={`${segment.title}.csv`}
            className={classNames(classes.inputAdornment, { [classes.errorState]: isError })}
            endAdornment={<InputAdornment position="end">{renderAdornment()}</InputAdornment>}
          />
        </FormControl>
        {renderCSVUpload()}
      </Grid>
    </Grid>
  );
};

export default OtherPeopleEdit;
